import {
  fallbackAnswer,
  scriptedAnswers,
  seededReportConversations,
  type DemoReportConversation,
  type ScriptedAnswer,
} from '../data/reports';
import { error, route, type DemoRoute } from '../router';

const conversations: DemoReportConversation[] = [...seededReportConversations];
let messageSeq = 100;

function normalize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2);
}

function pickAnswer(message: string): (ScriptedAnswer & { matched: boolean }) {
  const words = normalize(message);
  let best: ScriptedAnswer | null = null;
  let bestScore = 0;
  for (const answer of scriptedAnswers) {
    const score = words.filter((w) => answer.keywords.some((k) => w.includes(k) || k.includes(w))).length;
    if (score > bestScore) {
      bestScore = score;
      best = answer;
    }
  }
  if (best) return { ...best, matched: true };
  return { ...fallbackAnswer, keywords: [], matched: false };
}

export const reportHandlers: DemoRoute[] = [
  route('GET', '/reports/conversations', () =>
    conversations.map(({ id, title, client_id, updated_at }) => ({ id, title, client_id, updated_at }))
  ),
  route('GET', '/reports/conversations/:id', ({ params }) => {
    const conv = conversations.find((c) => c.id === params.id);
    return conv ?? error(404, 'Conversation not found');
  }),
  route('PATCH', '/reports/conversations/:id', ({ params, body }) => {
    const conv = conversations.find((c) => c.id === params.id);
    if (!conv) return error(404, 'Conversation not found');
    conv.title = (body as { title?: string }).title ?? conv.title;
    conv.updated_at = new Date().toISOString();
    const { id, title, client_id, updated_at } = conv;
    return { id, title, client_id, updated_at };
  }),
  route('DELETE', '/reports/conversations/:id', ({ params }) => {
    const idx = conversations.findIndex((c) => c.id === params.id);
    if (idx !== -1) conversations.splice(idx, 1);
    return { message: 'deleted' };
  }),

  route('POST', '/reports/chat', ({ body }) => {
    const { message, session_id, client_id } = (body ?? {}) as {
      message?: string;
      session_id?: string | null;
      client_id?: string;
    };
    const answer = pickAnswer(message ?? '');
    const now = new Date().toISOString();

    let conv = session_id ? conversations.find((c) => c.id === session_id) : undefined;
    if (!conv) {
      conv = {
        id: `report-conv-${Date.now()}`,
        title: answer.title,
        client_id: client_id ?? null,
        created_at: now,
        updated_at: now,
        messages: [],
      };
      conversations.unshift(conv);
    }
    conv.updated_at = now;
    conv.messages.push(
      {
        id: ++messageSeq,
        role: 'user',
        content: message ?? '',
        sql_query: null,
        result_data: null,
        result_columns: null,
        created_at: now,
      },
      {
        id: ++messageSeq,
        role: 'agent',
        content: answer.message,
        sql_query: answer.sql_query ?? null,
        result_data: answer.data ?? null,
        result_columns: answer.columns ?? null,
        created_at: now,
      }
    );

    return {
      success: true,
      message: answer.message,
      session_id: conv.id,
      conversation_id: conv.id,
      sql_query: answer.sql_query,
      data: answer.data,
      columns: answer.columns,
      steps: answer.steps,
    };
  }),
];
